import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Info, Trash2, Upload, ImageIcon, Loader2, Sparkles } from "lucide-react";
import { ProfileData } from "@/hooks/useProfileSettings";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ProfileImageCropper } from "@/components/onboarding/ProfileImageCropper";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { createCroppedImage, BANNER_ASPECT_RATIO, RECOMMENDED_BANNER_WIDTH, RECOMMENDED_BANNER_HEIGHT } from "@/utils/imageUtils";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";

const PROFESSIONS = [
  "Hairdresser / Hairstylist",
  "Nail Technician",
  "Makeup Artist",
  "Personal Trainer",
  "Massage Therapist",
  "Photographer",
  "Graphic Designer",
  "Social Media Manager",
  "Barber",
  "Videographer",
  "Coach",
  "Accountant",
  "MC (Master of Ceremonies)",
  "Moderator",
  "Other"
] as const;

interface BusinessSettingsProps {
  profile: ProfileData | null;
  loading: boolean;
  onUpdate: (updates: Partial<ProfileData>) => Promise<boolean>;
}

const BusinessSettings = ({
  profile,
  loading,
  onUpdate
}: BusinessSettingsProps) => {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const [companyName, setCompanyName] = useState(profile?.company_name || "");
  const [profession, setProfession] = useState(profile?.profession || "");
  const [customProfession, setCustomProfession] = useState("");
  const [profileBio, setProfileBio] = useState(profile?.profile_bio || "");
  
  const [previewProfileImage, setPreviewProfileImage] = useState<string | null>(profile?.profile_image_url || null);
  const [previewBannerImage, setPreviewBannerImage] = useState<string | null>(profile?.banner_image_url || null);
  
  const profileImageInputRef = useRef<HTMLInputElement>(null);
  const bannerImageInputRef = useRef<HTMLInputElement>(null);
  
  const [savingImages, setSavingImages] = useState(false);

  const [showProfileCropper, setShowProfileCropper] = useState(false);
  const [tempProfileImageUrl, setTempProfileImageUrl] = useState<string | null>(null);
  
  // Banner cropping states
  const [showBannerCropper, setShowBannerCropper] = useState(false);
  const [tempBannerImageUrl, setTempBannerImageUrl] = useState<string | null>(null);
  const [bannerCrop, setBannerCrop] = useState<Crop>({
    unit: '%',
    width: 100,
    height: (100 / BANNER_ASPECT_RATIO) * (BANNER_ASPECT_RATIO / 3),
    x: 0,
    y: 0,
  });
  const bannerImageRef = useRef<HTMLImageElement | null>(null);
  
  const location = useLocation();
  const missingFields: string[] = location.state?.missingFields || [];
  const refs = {
    "Business Name": useRef<HTMLInputElement>(null),
    "Profession": useRef<HTMLDivElement>(null),
    "Custom Profession": useRef<HTMLInputElement>(null),
    "Profile Bio": useRef<HTMLTextAreaElement>(null),
    "Profile Image": useRef<HTMLInputElement>(null),
    "Banner Image": useRef<HTMLInputElement>(null),
  };

  const [generatingBio, setGeneratingBio] = useState(false);
  const [generatingBusinessName, setGeneratingBusinessName] = useState(false);

  useEffect(() => {
    if (missingFields.length > 0 && refs[missingFields[0]]?.current) {
      refs[missingFields[0]].current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [missingFields, refs]);

  useEffect(() => {
    if (profile) {
      setCompanyName(profile.company_name || "");
      setProfession(profile.profession || "");
      setPreviewProfileImage(profile.profile_image_url || null);
      setPreviewBannerImage(profile.banner_image_url || null);
      setProfileBio(profile.profile_bio || "");
      
      if (profile.profession && !PROFESSIONS.includes(profile.profession as typeof PROFESSIONS[number])) {
        setCustomProfession(profile.profession);
      }
    }
  }, [profile]);

  const handleSave = async () => {
    try {
      const professionToSave = profession === "Other" ? customProfession : profession;
      
      const success = await onUpdate({
        company_name: companyName,
        profession: professionToSave,
        profile_bio: profileBio
      });
      
      if (success) {
        toast({
          title: "Success",
          description: "Business information updated successfully",
        });
      }
    } catch (error) {
      console.error("Error saving business settings:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save business information",
      });
    }
  };
  
  const handleProfessionChange = (value: string) => {
    setProfession(value);
    if (value !== "Other") {
      setCustomProfession("");
    }
  };
  
  const handleProfileImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const imageUrl = URL.createObjectURL(file);
    setTempProfileImageUrl(imageUrl);
    setShowProfileCropper(true);
  };

  const handleDeleteProfileImage = async () => {
    setSavingImages(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");
      
      // Extract filename from URL if possible
      let filePath = "";
      if (profile?.profile_image_url) {
        const url = new URL(profile.profile_image_url);
        const pathParts = url.pathname.split('/');
        if (pathParts.length > 0 && pathParts[pathParts.length - 1].includes('profile-')) {
          filePath = pathParts[pathParts.length - 1];
        }
      }
      
      // Try to delete from storage if we have a filename
      if (filePath) {
        const { error: storageError } = await supabase.storage
          .from('profile_images')
          .remove([filePath]);
          
        if (storageError) {
          console.error("Error removing image from storage:", storageError);
        }
      }
      
      // Update profile in database
      await onUpdate({ profile_image_url: null });
      
      // Update UI
      setPreviewProfileImage(null);
      
      toast({
        title: "Success",
        description: "Profile image removed successfully",
      });
    } catch (error) {
      console.error("Error removing profile image:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to remove profile image",
      });
    } finally {
      setSavingImages(false);
    }
  };

  const handleCropComplete = async (croppedImageUrl: string) => {
    setShowProfileCropper(false);
    
    setPreviewProfileImage(croppedImageUrl);
    
    setSavingImages(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");
      
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], "profile.jpg", { type: "image/jpeg" });
      
      const fileExt = "jpg";
      const filePath = `profile-${user.id}-${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('profile_images')
        .upload(filePath, file);
        
      if (uploadError) throw uploadError;
      
      const { data: urlData } = supabase.storage
        .from('profile_images')
        .getPublicUrl(filePath);
        
      if (urlData?.publicUrl) {
        await onUpdate({ profile_image_url: urlData.publicUrl });
        toast({
          title: "Success",
          description: "Profile image updated successfully",
        });
      }
    } catch (error) {
      console.error("Error uploading profile image:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to upload profile image",
      });
      setPreviewProfileImage(profile?.profile_image_url || null);
    } finally {
      setSavingImages(false);
      setTempProfileImageUrl(null);
    }
  };
  
  const handleBannerImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Create URL for the cropper
    const imageUrl = URL.createObjectURL(file);
    setTempBannerImageUrl(imageUrl);
    setShowBannerCropper(true);
  };

  const handleDeleteBannerImage = async () => {
    setSavingImages(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");
      
      // Extract filename from URL if possible
      let filePath = "";
      if (profile?.banner_image_url) {
        const url = new URL(profile.banner_image_url);
        const pathParts = url.pathname.split('/');
        if (pathParts.length > 0 && pathParts[pathParts.length - 1].includes('banner-')) {
          filePath = pathParts[pathParts.length - 1];
        }
      }
      
      // Try to delete from storage if we have a filename
      if (filePath) {
        const { error: storageError } = await supabase.storage
          .from('profile_images')
          .remove([filePath]);
          
        if (storageError) {
          console.error("Error removing banner from storage:", storageError);
        }
      }
      
      // Update profile in database
      await onUpdate({ banner_image_url: null });
      
      // Update UI
      setPreviewBannerImage(null);
      
      toast({
        title: "Success",
        description: "Banner image removed successfully",
      });
    } catch (error) {
      console.error("Error removing banner image:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to remove banner image",
      });
    } finally {
      setSavingImages(false);
    }
  };

  const handleBannerCropComplete = async () => {
    if (!bannerImageRef.current || !tempBannerImageUrl || !bannerCrop.width || !bannerCrop.height) {
      setShowBannerCropper(false);
      return;
    }
    
    setSavingImages(true);
    
    try {
      // Create cropped image blob
      const croppedImageBlob = await createCroppedImage(bannerImageRef.current, bannerCrop);
      
      // Preview the cropped image
      const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
      setPreviewBannerImage(croppedImageUrl);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");
      
      // Create file from blob for upload
      const file = new File([croppedImageBlob], "banner.jpg", { type: "image/jpeg" });
      
      const filePath = `banner-${user.id}-${Date.now()}.jpg`;
      
      const { error: uploadError } = await supabase.storage
        .from('profile_images')
        .upload(filePath, file);
        
      if (uploadError) {
        console.error("Upload error details:", uploadError);
        throw uploadError;
      }
      
      const { data: urlData } = supabase.storage
        .from('profile_images')
        .getPublicUrl(filePath);
        
      if (urlData?.publicUrl) {
        await onUpdate({ banner_image_url: urlData.publicUrl });
        toast({
          title: "Success",
          description: "Banner image updated successfully",
        });
      }
    } catch (error) {
      console.error("Error uploading banner image:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to upload banner image. Please try again.",
      });
    } finally {
      setSavingImages(false);
      setShowBannerCropper(false);
      setTempBannerImageUrl(null);
    }
  };

  const handleAvatarClick = () => {
    profileImageInputRef.current?.click();
  };

  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Business Images</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Profile Picture</Label>
              <div className="flex flex-col items-center space-y-3 p-4 border rounded-md">
                <div className="relative">
                  <Avatar 
                    className="w-24 h-24 cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={handleAvatarClick}
                  >
                    {previewProfileImage ? (
                      <AvatarImage src={previewProfileImage} alt="Profile" />
                    ) : (
                      <AvatarFallback style={{ background: "linear-gradient(to right, #FF1493, #FFA500)" }} className="text-white">
                        <ImageIcon className="w-12 h-12 text-white" />
                      </AvatarFallback>
                    )}
                  </Avatar>
                  
                  {previewProfileImage && (
                    <button 
                      onClick={handleDeleteProfileImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                      title="Delete image"
                      type="button"
                      disabled={savingImages}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={handleAvatarClick}
                  className="mt-2"
                  disabled={savingImages}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {t('settings.uploadPicture')}
                </Button>
                <input
                  type="file"
                  ref={profileImageInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleProfileImageChange}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Label>{t('settings.bannerImage')}</Label>
                <div className="flex items-center text-xs text-muted-foreground">
                  <Info className="w-3 h-3 mr-1" />
                  {t('settings.recommendedSize')}: {RECOMMENDED_BANNER_WIDTH}x{RECOMMENDED_BANNER_HEIGHT}px (3:1 ratio)
                </div>
              </div>
              <div className="p-4 border rounded-md space-y-3">
                <div className="relative">
                  <div 
                    className="aspect-[3/1] bg-muted rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => bannerImageInputRef.current?.click()}
                  >
                    {previewBannerImage ? (
                      <img 
                        src={previewBannerImage} 
                        alt="Banner" 
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange bg-opacity-10 rounded-lg">
                        <ImageIcon className="w-12 h-12 text-white" />
                      </div>
                    )}
                  </div>
                  
                  {previewBannerImage && (
                    <button 
                      onClick={handleDeleteBannerImage}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                      title="Delete banner"
                      type="button"
                      disabled={savingImages}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => bannerImageInputRef.current?.click()}
                  className="mt-2"
                  disabled={savingImages}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {t('settings.uploadBanner')}
                </Button>
                <input
                  type="file"
                  ref={bannerImageInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleBannerImageChange}
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <h3 className="text-lg font-medium">{t('settings.businessInformation')}</h3>
          
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="company_name">{t('settings.businessName')}</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1.5 text-xs bg-gradient-to-r from-gebeya-pink/10 to-gebeya-orange/10 border-gebeya-pink/20 hover:bg-gradient-to-r hover:from-gebeya-pink/20 hover:to-gebeya-orange/20"
                  onClick={async () => {
                    setGeneratingBusinessName(true);
                    try {
                      const { data, error } = await supabase.functions.invoke('generate-business-name', {
                        body: { profession, existing_name: companyName, locale: i18n.language },
                      });
                      console.log('AI business name response:', data);
                      if (error) throw error;
                      if (data) {
                        if (typeof data === 'string') {
                          setCompanyName(data);
                        } else if (data.name) {
                          setCompanyName(data.name);
                        } else if (data.business_name) {
                          setCompanyName(data.business_name);
                        } else if (data.businessName) {
                          setCompanyName(data.businessName);
                        } else {
                          toast({
                            title: 'No Name Generated',
                            description: 'AI did not return a business name. Please try again.',
                            variant: 'destructive',
                          });
                        }
                      } else {
                        toast({
                          title: 'No Name Generated',
                          description: 'AI did not return a business name. Please try again.',
                          variant: 'destructive',
                        });
                      }
                    } catch (err) {
                      toast({
                        title: 'Generation Failed',
                        description: 'Could not generate business name. Please try again.',
                        variant: 'destructive',
                      });
                    } finally {
                      setGeneratingBusinessName(false);
                    }
                  }}
                  disabled={generatingBusinessName}
                >
                  {generatingBusinessName ? (
                    <>
                      <img 
                        src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
                        alt={t('generating')} 
                        className="h-3.5 w-3.5 gebeya-spinner" 
                      />
                      <span className="text-gebeya-pink">{t('services.generating')}</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5 text-gebeya-pink" />
                      <span className="text-gebeya-pink">{t('services.generateWithAI')}</span>
                    </>
                  )}
                </Button>
              </div>
              <Input 
                id="company_name" 
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                ref={refs["Business Name"]}
                className={missingFields.includes("Business Name") ? "border-red-500" : ""}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="profession">{t('settings.profession')}</Label>
              <div
                ref={refs["Profession"]}
                className={missingFields.includes("Profession") ? "border-red-500 rounded p-1" : ""}
              >
                <Select
                  value={profession}
                  onValueChange={handleProfessionChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('settings.selectProfession')} />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-[300px] overflow-y-auto">
                    <SelectGroup>
                      <SelectLabel>{t('settings.availableProfessions')}</SelectLabel>
                      {PROFESSIONS.map((prof) => (
                        <SelectItem key={prof} value={prof}>
                          {prof === "Other" ? <strong>Other</strong> : prof}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              
              {profession === "Other" && (
                <div className="mt-2">
                  <Input
                    id="customProfession"
                    placeholder={t('settings.enterProfession')}
                    value={customProfession}
                    onChange={(e) => setCustomProfession(e.target.value)}
                    required={profession === "Other"}
                    ref={refs["Custom Profession"]}
                    className={missingFields.includes("Profession") ? "border-red-500" : ""}
                  />
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="profile_bio">{t('settings.profileBio')}</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1.5 text-xs bg-gradient-to-r from-gebeya-pink/10 to-gebeya-orange/10 border-gebeya-pink/20 hover:bg-gradient-to-r hover:from-gebeya-pink/20 hover:to-gebeya-orange/20"
                  onClick={async () => {
                    setGeneratingBio(true);
                    try {
                      const services = profile?.services?.map(s => s.name) || [];
                      const serviceDescriptions = profile?.services?.map(s => s.description) || [];
                      const { data, error } = await supabase.functions.invoke('generate-business-bio', {
                        body: {
                          firstName: profile?.first_name,
                          lastName: profile?.last_name,
                          businessName: companyName,
                          profession: profession === "Other" ? customProfession : profession,
                          country: profile?.country,
                          services,
                          serviceDescriptions,
                          locale: i18n.language
                        },
                      });
                      if (error) throw error;
                      if (data && data.bio) setProfileBio(data.bio);
                    } catch (err) {
                      // Optionally show error toast
                    } finally {
                      setGeneratingBio(false);
                    }
                  }}
                  disabled={generatingBio}
                >
                  {generatingBio ? (
                    <>
                      <img 
                        src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
                        alt={t('generating')} 
                        className="h-3.5 w-3.5 gebeya-spinner" 
                      />
                      <span className="text-gebeya-pink">{t('services.generating')}</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5 text-gebeya-pink" />
                      <span className="text-gebeya-pink">{t('services.generateWithAI')}</span>
                    </>
                  )}
                </Button>
              </div>
              <textarea
                id="profile_bio"
                className={`form-textarea ${missingFields.includes("Profile Bio") ? "border-red-500" : ""} w-full min-h-[120px] p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gebeya-pink`}
                value={profileBio}
                onChange={(e) => {
                  setProfileBio(e.target.value);
                }}
                placeholder={t('settings.profileBioPlaceholder')}
                maxLength={1500}
                ref={refs["Profile Bio"]}
              />
              <p className="text-xs text-muted-foreground">
                {t('settings.profileBioDescription')}
              </p>
            </div>
          </div>
        </div>
        
        <Button 
          className="mt-4 bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 transition-opacity text-white"
          onClick={handleSave}
          disabled={loading || savingImages || (profession === "Other" && !customProfession)}
        >
          {loading || savingImages ? t('settings.saving') : t('settings.saveBusinessInformation')}
        </Button>

        {/* Profile image cropper dialog */}
        {showProfileCropper && tempProfileImageUrl && (
          <ProfileImageCropper
            open={showProfileCropper}
            onClose={() => {
              setShowProfileCropper(false);
              setTempProfileImageUrl(null);
            }}
            imageUrl={tempProfileImageUrl}
            onCropComplete={handleCropComplete}
          />
        )}

        {/* Banner image cropping dialog */}
        {showBannerCropper && tempBannerImageUrl && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold mb-4">Crop Banner Image</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Drag to reposition. The image will be cropped to a 3:1 ratio for optimal display on your service page.
              </p>
              
              <div className="mb-6 bg-gray-100 p-2 rounded-md max-w-full overflow-hidden">
                <ReactCrop
                  crop={bannerCrop}
                  onChange={(newCrop) => setBannerCrop(newCrop)}
                  aspect={BANNER_ASPECT_RATIO}
                  className="max-w-full"
                >
                  <img 
                    ref={bannerImageRef}
                    src={tempBannerImageUrl} 
                    alt="Crop Banner"
                    className="max-w-full"
                  />
                </ReactCrop>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBannerCropper(false);
                    setTempBannerImageUrl(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBannerCropComplete}
                  disabled={!bannerCrop.width || !bannerCrop.height}
                >
                  Apply Crop
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BusinessSettings;
